import React, { Fragment, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient, graphql } from 'react-apollo';
import debounce from 'debounce-promise';
import compose from 'lodash.flowright';
import uniqBy from 'lodash.uniqby';

import MultiSelect from '../../../../../components/global/MultiSelect';
import IdentityOptionLabel from './IdentityOptionLabel';
import { AlbumSelectedIdentitiesForFaceTagging } from '../graphql/queries';
import withModal from '../../../../../components/global/modal/withModal';
import Button from '../../../../../components/global/Button';
import { getErrorString } from '../../../../../util/normalize-errors';
import Alert from '../../../../../components/global/Alert';
import Spinner from '../../../../../components/global/Spinner';
import ContextualString from '../../../../../components/contextual/ContextualString';
import { VOTE_DECISION } from '../../../../../constants/vote-decisions';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumId: PropTypes.string.isRequired,
  onSelectIdentity: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  actionProps: PropTypes.shape({
    face: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  loadingIdentities: PropTypes.bool,
  identities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
};

const FaceTagIdentitySelection = ({
  albumId,
  album,
  onSelectIdentity,
  onRequestClose,
  actionProps: { face, humanLabeledPubBox, currentIdentity } = {},
  loadingIdentities,
  identities,
}) => {
  const [identity, setIdentity] = useState(null);
  const [error, setError] = useState('');
  const client = useApolloClient();

  const defaultOptions = useMemo(
    () => [
      {
        label: 'Recent',
        options: identity
          ? uniqBy([identity, ...(identities ?? [])], 'id')
          : identities ?? [],
      },
    ],
    [identities],
  );

  const getIdentities = async searchQuery => {
    setError('');

    try {
      const {
        data: { album: albumRecord },
      } = await client.query({
        query: AlbumSelectedIdentitiesForFaceTagging,
        variables: {
          id: albumId,
          limit: 10,
          searchQuery,
        },
        fetchPolicy: 'no-cache',
      });

      return (albumRecord?.selected_identities?.records ?? [])
        .filter(
          // Avoid allowing to select users with no faces. No votes should be casted on these.
          selectedIdentity =>
            !!selectedIdentity.identity?.current_face?.id ||
            !!selectedIdentity.identity?.current_matching_face?.id,
        )
        .map(selectedIdentity => selectedIdentity.identity);
    } catch (err) {
      setError(getErrorString(err));
      return [];
    }
  };

  const debouncedGetIdentities = debounce(getIdentities, 300);
  const [matchedPhoto] = face.matched_photos.records;

  if (loadingIdentities) {
    return (
      <div className="d-flex p-5">
        <Spinner className="m-auto" />
      </div>
    );
  }

  const loadOptions = async (...args) => {
    const results = await debouncedGetIdentities(...args);
    return results;
  };

  return (
    <div>
      <h4 className="text-center">Who is this?</h4>
      {!face.has_human_votes && matchedPhoto && (
        <p className="my-2">
          The identity on this photo has been matched by our internal algorithm.
          By manually selecting an identity, you will be overwriting that
          decision.
        </p>
      )}

      {error && <Alert type="danger">{error}</Alert>}
      <MultiSelect
        key={`identity-select-${albumId}`}
        id="identity-multi-select"
        label="Search for Identity"
        labelSrOnly
        placeholder="Enter first or last name..."
        className="identity-selection face-tag-identity-selection w-100 text-dark mb-1"
        selectClassName="multi-select-control-lg"
        noOptionsMessage={() => 'No identities meet your search criteria...'}
        value={identity}
        defaultOptions={defaultOptions}
        loadOptions={loadOptions}
        onKeyDown={event => {
          if (event.key === 'Shift') {
            event.stopPropagation();
            event.preventDefault();
          }
        }}
        formatOptionLabel={(identityRecord, { context }) => (
          <IdentityOptionLabel identity={identityRecord} context={context} />
        )}
        getOptionValue={identityRecord => identityRecord.id || identityRecord}
        onChange={identityRecord => {
          if (!currentIdentity) {
            onSelectIdentity({
              face,
              identity: identityRecord,
              decision: VOTE_DECISION.TOTALLY,
              humanLabeledPubBox,
            });
            onRequestClose();
          } else {
            setIdentity(identityRecord);
          }
        }}
        cacheOptions={false}
        autoFocus
        openMenuOnFocus={!identity}
      />
      <p className="text-xs">
        <span className="text-extra-bold text-dark">Note:</span> Album{' '}
        <ContextualString
          id="albumHeaderMembersLabel"
          defaultMessage="members"
          type={album.album_type}
          subtype={album.album_subtype}
        />{' '}
        with no available selfies will not be displayed.
      </p>

      <div className="mt-2 d-flex flex-column flex-md-row justify-content-center">
        {currentIdentity && face && (
          <Fragment>
            <Button
              onClick={() => {
                onSelectIdentity({
                  face,
                  identity: currentIdentity,
                  decision: VOTE_DECISION.NOPE,
                  humanLabeledPubBox,
                });
                onRequestClose();
              }}
              className="mr-0 mr-md-1 mb-1 mb-md-0"
              btnStyle="danger"
            >
              Remove
            </Button>
            <Button
              onClick={() => {
                onSelectIdentity({
                  face,
                  identity,
                  decision: VOTE_DECISION.TOTALLY,
                  humanLabeledPubBox,
                });
                onRequestClose();
              }}
              disabled={!identity}
            >
              Confirm
            </Button>
          </Fragment>
        )}
      </div>
    </div>
  );
};

FaceTagIdentitySelection.propTypes = propTypes;

const withAlbumSelectedIdentitiesForFaceTagging = graphql(
  AlbumSelectedIdentitiesForFaceTagging,
  {
    options: ({ albumId }) => ({
      variables: {
        id: albumId,
        limit: 10,
      },
      fetchPolicy: 'cache-and-network',
    }),
    props: ({ data: { album, loading } }) => ({
      album,
      identities:
        (album?.selected_identities?.records ?? [])
          .filter(
            // Avoid allowing to select users with no faces. No votes should be casted on these.
            selectedIdentity => !!selectedIdentity.identity?.current_face?.id,
          )
          .map(selectedIdentity => selectedIdentity.identity) ?? [],
      loadingIdentities: loading,
    }),
  },
);

const FACE_TAG_IDENTITY_SELECTION_MODAL = 'modal/FACE_TAG_IDENTITY_SELECTION';

export {
  FaceTagIdentitySelection as FaceTagIdentitySelectionComponent,
  FACE_TAG_IDENTITY_SELECTION_MODAL,
};

export default withModal(
  compose(withAlbumSelectedIdentitiesForFaceTagging)(FaceTagIdentitySelection),
  {
    name: FACE_TAG_IDENTITY_SELECTION_MODAL,
    size: 'sm',
    type: 'full',
    className: 'face-tag-identity-selection-modal',
  },
);
